import { Injectable, Injector } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { PositioningService } from 'ngx-extended-pdf-viewer';

import { ModalBaseComponent } from '../../../gamma/core/components/modal-base.component';
import { I18nService } from '../../../gamma/i18n';
import { LogicalUnitModalBaseComponent } from '../../../gamma/logicalunit/logicalunit-modal-base.component';
import { IOpenModalLogicalUnitParams } from '../../../gamma/logicalunit/logicalunit-modal.interface';
import { IModalAlertOptions, IModalConfirmOptions, IModalOptions, IModalProvider, IOpenModalComponentParams, ModalService } from '../../../gamma/modal';
import { ModalDismissGuard } from '../../../gamma/modal/modal-dismiss.guard';
import { IModalTemplateContext, ModalTemplateComponent } from '../../../gamma/modal/modal-template/modal-template.component';
import { UserAgentService } from '../../../gamma/utils';
import { U2000_ModalCallerInjector } from './U2000_modal-caller.injector';

/**
 * Modal provider built on ngx-bootstrap (`BsModalService`/`BsModalRef`), replacing the previous
 * ngx-modialog-7-based implementation — see the "Groupe A0" section of
 * docs/components/SAIA-23489-impl-plan.md for the migration rationale and the reference
 * implementation this was adapted from (saia-ui/adsu2's A2000_ModalProvider).
 */
@Injectable()
export class U2000_ModalProvider implements IModalProvider {
    service: ModalService;

    private i18nService: I18nService;
    private translateService: TranslateService;
    private openedDialogs: BsModalRef<any>[];

    constructor(
        public injector: Injector,
        private bsModal: BsModalService,
        private userAgentService: UserAgentService,
        private modalDismissGuard: ModalDismissGuard,
        private modalCallerInjector: U2000_ModalCallerInjector,
    ) {
        this.openedDialogs = [];
    }

    initInjectedElement() {
        if (this.i18nService == null) {
            this.i18nService = this.injector.get(I18nService);
        }
        if (this.translateService == null) {
            this.translateService = this.injector.get(TranslateService);
        }
    }

    alert(options: IModalAlertOptions): Promise<any> {
        this.initInjectedElement();

        const modalOptions = this.buildDisplayModalOptions(options);
        modalOptions.initialState = {
            title: options && options.title,
            message: options && options.message,
            // okButtonText/cancelButtonText, unlike title/message, are NOT pre-translated by
            // ModalService.translateOptions() — a caller may pass either an already-resolved
            // string or a raw i18n key (e.g. U4122_BankAccountDetailFormGuard passes
            // 'lblContinue'), matching the previous provider's applyModalOptions() behavior.
            okButtonText: (options && options.okButtonText && this.i18nService.formatLabel(options.okButtonText)) || this.translateService.instant('lblOK'),
            cancelButtonText: null,
            hideCancelButton: true,
        } as IModalTemplateContext;

        const modalRef = this.bsModal.show(ModalTemplateComponent, modalOptions);

        return new Promise<any>(resolve => {
            modalRef.onHidden.subscribe(() => {
                resolve(undefined);
            });
        });
    }

    confirm(options: IModalConfirmOptions): Promise<any> {
        this.initInjectedElement();

        if (!options.title) {
            options.title = this.translateService.instant('lblConfirm');
        }

        const modalOptions = this.buildDisplayModalOptions(options);
        modalOptions.initialState = {
            title: options.title,
            message: options.message,
            // See alert() above — okButtonText/cancelButtonText may be raw i18n keys.
            okButtonText: (options.okButtonText && this.i18nService.formatLabel(options.okButtonText)) || this.translateService.instant('lblOK'),
            cancelButtonText: (options.cancelButtonText && this.i18nService.formatLabel(options.cancelButtonText)) || this.translateService.instant('lblCancel'),
            hideCancelButton: false,
        } as IModalTemplateContext;

        const modalRef = this.bsModal.show(ModalTemplateComponent, modalOptions);

        return new Promise<any>((resolve, reject) => {
            modalRef.onHidden.subscribe(() => {
                if ((modalRef.content as ModalTemplateComponent).result === true) {
                    resolve(undefined);
                } else {
                    reject();
                }
            });
        });
    }

    openComponent<U>(params: IOpenModalComponentParams, callback?: (result: U) => void, cancelCallback?: () => void) {
        const logicalUnitParams = params as IOpenModalLogicalUnitParams<any>;
        // Display options (size/backdrop) were previously set by the leaf component itself,
        // mutating `dialog.context` from its own constructor — too late under ngx-bootstrap,
        // where they must be known before `.show()` creates the modal. Each converted leaf
        // component instead declares them as a static `modalDisplayOptions` property (see e.g.
        // U4122_BankAccountDetailModalComponent), read here before the component is created —
        // this keeps the migration confined to the 121 leaf files, without having to touch every
        // call site that opens one (unlike adsu2's reference implementation, which moved these to
        // the caller — not viable here given how many call sites this app has per modal).
        const componentDisplayOptions: IModalOptions = (params.component as any).modalDisplayOptions;
        const modalOptions = this.buildDisplayModalOptions({ ...componentDisplayOptions, ...params.options });
        // The created component reads its custom per-modal data as `this.context` (see leaf
        // modal components, e.g. U4122_BankAccountDetailModalComponent) — mirrors the previous
        // `dialog.context` from ngx-modialog-7.
        modalOptions.initialState = { context: params.options };
        // PositioningService here is ngx-extended-pdf-viewer's OWN internal class of that name
        // (lib/dynamic-css/positioning.service — a same-name coincidence with ngx-bootstrap's own
        // unrelated PositioningService, NOT the actual cause despite the identical error message).
        // It is undecorated (no @Injectable/providedIn) like NgxExtendedPdfViewerService itself
        // (see the per-call-site fix on U2000_BankAccountManualInfoModalComponent et al.) and is
        // now a hard constructor dependency of PdfStampEditorComponent — a NEW internal toolbar
        // feature that appeared between ngx-extended-pdf-viewer 20.5.2 and 23.3.1 (SAIA-23489 Hop 6)
        // and is always instantiated as part of <ngx-extended-pdf-viewer>, unconditionally. Any
        // modal whose content renders <ngx-extended-pdf-viewer> now throws `NG0201: No provider
        // found for PositioningService` without this (discovered on U6141_addattachment-form's PDF
        // preview during Hop 6's exhaustive verification — see the plan). Provided centrally here,
        // alongside NgxExtendedPdfViewerService's existing per-call-site providers (both are needed
        // together wherever the viewer renders, so this makes those individual additions redundant
        // but harmless — left as-is to minimize this fix's footprint) and for any future modal
        // content using the PDF viewer without its author realizing this gap exists otherwise.
        modalOptions.providers = [{ provide: PositioningService, useClass: PositioningService }, ...(params.providers ?? [])];
        if (logicalUnitParams.resolver != null) {
            modalOptions.closeInterceptor = this.makeIdempotentInterceptor(this.modalDismissGuard.asCloseInterceptor());
        }

        // Created against the opener's injector (see U2000_ModalCallerInjector) so module-scoped
        // services resolve; `modalOptions.providers` above still take precedence.
        const modalRef = this.modalCallerInjector.runWith(params.injector, () => this.bsModal.show(params.component as any, modalOptions));
        if (modalOptions.closeInterceptor) {
            this.wireCloseInterceptor(modalRef, modalOptions.closeInterceptor);
        }

        /* iOS hack, must deactivate/reactivate the overflow prop.,
        otherwise the modal cannot scroll in portrait resolution in some cases.*/
        if (this.userAgentService.isMobile()) {
            setTimeout(() => {
                document.getElementsByTagName('modal-container')[0]['style'].overflowY = 'hidden';
            }, 1);

            setTimeout(() => {
                document.getElementsByTagName('modal-container')[0]['style'].overflowY = 'auto';
            }, 100);
        }
        this.openedDialogs.push(modalRef);

        modalRef.onHidden.subscribe(() => {
            const index = this.openedDialogs.indexOf(modalRef);
            if (index !== -1) {
                this.openedDialogs.splice(index, 1);
            }

            const content = modalRef.content as unknown as ModalBaseComponent<any, U> | LogicalUnitModalBaseComponent<any, U>;
            if (content != null && content.result != null) {
                if (callback) {
                    callback(content.result);
                }
            } else {
                if (cancelCallback) {
                    cancelCallback();
                }
            }
        });
    }

    /**
     * ngx-bootstrap 10.x regression (undocumented, verified against the bundled source of both
     * versions — not in the official changelog): in 9.0.0, `BsModalRef.hide` called
     * `ModalContainerComponent.hide()` directly, which checks `ModalOptions.closeInterceptor`
     * before closing. In 10.x-12.x, `BsModalRef.hide` instead called `BsModalService.hide(id)`,
     * which never checked `closeInterceptor` — only the ESC/backdrop/popstate paths (wired
     * directly to `ModalContainerComponent`'s own DOM listeners, never through `BsModalRef`)
     * still honored it. This silently broke `ModalDismissGuard` for every `this.dialog.hide()`
     * call site (the standard close/cancel-button pattern used by all 121 modals converted in
     * Groupe A0).
     *
     * This re-wires `BsModalRef.hide` to run the interceptor first, then delegate to
     * ngx-bootstrap's own implementation — restoring the contract regardless of which of the two
     * shapes above `BsModalRef.hide` happens to have in the installed version. At Hop 4
     * (Angular 18, ngx-bootstrap 18.1.3), `BsModalRef.hide` reverted BACK to the 9.0.0 shape
     * (`() => modalContainerRef.instance?.hide()`, itself interceptor-aware) — meaning this
     * wrapper and ngx-bootstrap's own check would otherwise both run the interceptor, once each,
     * for a single `this.dialog.hide()` call (confirmed by e2e regression: the "confirm and
     * close" path of `U4122_BankAccountDetailDismissGuardTest` failed, consistent with a second,
     * unexpected confirmation prompt on the second, redundant check). Rather than tracking which
     * ngx-bootstrap version does what at every future hop, `closeInterceptor` itself is made
     * idempotent (see {@link makeIdempotentInterceptor}) before being handed to both this wrapper
     * and ngx-bootstrap's own config — so however many times anything invokes it for one close
     * attempt, the underlying guard logic (and any confirmation UI it opens) only actually runs
     * once, safely, independent of ngx-bootstrap's internal wiring in any given version.
     */
    private wireCloseInterceptor(modalRef: BsModalRef<any>, closeInterceptor: () => Promise<void>) {
        const originalHide = modalRef.hide.bind(modalRef);
        modalRef.hide = () => {
            Promise.resolve(closeInterceptor()).then(
                () => originalHide(),
                () => undefined,
            );
        };
    }

    /**
     * Wraps a `closeInterceptor` so concurrent/rapid-sequential invocations for the same close
     * attempt share a single in-flight call, and a resolved ("allow close") outcome is cached
     * permanently rather than re-running the underlying guard logic — see
     * {@link wireCloseInterceptor} for why this matters (ngx-bootstrap may itself also invoke
     * `ModalOptions.closeInterceptor` directly, in addition to this provider's own wrapped
     * `BsModalRef.hide`, depending on the installed version). A first implementation reset the
     * cache via `.finally()` on settlement — which cleared it (via a microtask, running strictly
     * before a `.then()` chained on the same call) *before* the second, redundant caller could
     * ever observe the cached value, defeating the point: confirmed by e2e regression
     * (`confirmingTheConfirmation_closesTheModal`) — the guard's confirmation dialog re-opened a
     * second time because the redundant call re-ran it from scratch. Only a rejection ("blocked",
     * e.g. user cancelled) resets the cache — a later, genuinely distinct close attempt should
     * re-check the guard against the form's now-current state; an allowed close never needs to
     * run the check again, the modal is being destroyed either way.
     */
    private makeIdempotentInterceptor(closeInterceptor: () => Promise<void>): () => Promise<void> {
        let pending: Promise<void> | null = null;
        return () => {
            if (!pending) {
                pending = closeInterceptor().catch(err => {
                    pending = null;
                    throw err;
                });
            }
            return pending;
        };
    }

    closeAll() {
        this.openedDialogs.forEach(x => {
            x.hide();
        });
        this.openedDialogs = [];
    }

    hideAll(): void {
        throw new Error('Dialog not supported by the provider');
    }

    /**
     * Translates the app's own {@link IModalOptions} display flags (size/backdrop/dialogClass —
     * set by a caller or, previously, mutated by a leaf component's constructor via
     * `dialog.context`) into ngx-bootstrap's `ModalOptions`. Does not set `initialState` — callers
     * assign it themselves (flat for the generic alert/confirm template, nested under `context`
     * for an arbitrary opened component).
     */
    private buildDisplayModalOptions(options: IModalOptions): ModalOptions<any> {
        const modalOptions: ModalOptions<any> = {};

        if (options) {
            if (options.size === 'small') {
                modalOptions.class = 'modal-sm';
            } else if (options.size === 'large') {
                modalOptions.class = 'modal-lg';
            }
            if (options.dialogClass) {
                modalOptions.class = [modalOptions.class, options.dialogClass].filter(x => !!x).join(' ');
            }
            if (options.backdrop) {
                modalOptions.backdrop = 'static';
            }
        }

        return modalOptions;
    }

    scrollTop() {
        document.getElementsByTagName('modal-container')[0].scrollTop = 0;
    }
}
